import {
  Flex,
  HStack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from '@chakra-ui/react';

import { fmtUSD } from '../format';
import type { Scenario } from '../types';
import { type ScenarioDraft, ScenarioPopover } from './ScenarioPopover';

interface ScenarioChipProps {
  scenario: Scenario;
  onUpdate: (draft: ScenarioDraft) => void;
  onRemove: () => void;
}

// Mirrors the dashboard's CustomDash FilterTag chip exactly: a small
// (24px) solid Tag with a navy keyword, gray • connectors, light-blue
// values, and the themed close button. Clicking it reopens the form
// prefilled; the × removes it.
export function ScenarioChip({
  scenario,
  onUpdate,
  onRemove,
}: ScenarioChipProps) {
  return (
    <ScenarioPopover
      initial={scenario}
      submitLabel="Update"
      confirmTestId="edit-scenario-confirm"
      onSubmit={onUpdate}
    >
      <Tag
        size="sm"
        variant="solid"
        height="24px"
        borderRadius="4px"
        bg="white"
        cursor="pointer"
        // The dashboard FilterTag uses a white border (invisible) because it
        // sits in a gray filter bar; this chip sits on the white page, so it
        // needs a visible border at rest to read as a white pill.
        border="1px solid"
        borderColor="gray.200"
        _hover={{
          bg: 'white',
          borderColor: 'gray.300',
          transition: 'all 0.2s ease-in-out',
        }}
        title="Edit scenario"
        data-testid="scenario-chip"
      >
        <Flex alignItems="center" minH="24px">
          <TagLabel>
            <HStack spacing={1}>
              <Text as="span" color="text.primary" fontWeight={600}>
                Investment amount
              </Text>
              <Text color="gray.500" fontSize="xs">
                •
              </Text>
              <Text color="gray.500" fontSize="xs">
                for
              </Text>
              <Text as="span" color="lightBlue.600" fontWeight={600}>
                {scenario.geo.name}, {scenario.industry}
              </Text>
              <Text color="gray.500" fontSize="xs">
                •
              </Text>
              <Text as="span" color="lightBlue.600" fontWeight={600}>
                {fmtUSD(scenario.amount)}
              </Text>
            </HStack>
          </TagLabel>
          <TagCloseButton
            ml={1}
            data-testid="scenario-chip-remove"
            onClick={(e) => {
              // Don't open the edit popover when removing.
              e.stopPropagation();
              onRemove();
            }}
          />
        </Flex>
      </Tag>
    </ScenarioPopover>
  );
}
